'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, Calendar, Star, Clock, Monitor, Download, AlertCircle } from 'lucide-react';
import type { TMDBMovie } from '../lib/tmdb';
import { getImageUrl, IMAGE_SIZES } from '../lib/tmdb';
import { getApiUrl } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { useIsTVDevice } from '../hooks/useTVNavigation';
import { getStreamSources } from '../lib/streamSources';
import { debugLog, debugWarn, debugError } from '../lib/debug';

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, config: any) => any;
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface Movie {
  id: string;
  title: string;
  size: string;
  quality: string;
  year: string;
  format: string;
  cached?: boolean;
  cacheInfo?: {
    gdriveFileId: string;
    uploadedAt: number;
    accessCount: number;
  } | null;
}

interface TVMovieDetailsPageProps {
  movie: TMDBMovie | null;
  onBack: () => void;
  onSearchBot: (movieTitle: string) => Promise<Movie[]>;
  onSelectVersion: (movie: Movie) => void;
  onMovieClick?: (movie: TMDBMovie) => void;
}

export default function TVMovieDetailsPage({
  movie,
  onBack,
  onSearchBot,
  onSelectVersion,
  onMovieClick,
}: TVMovieDetailsPageProps) {
  const [botResults, setBotResults] = useState<Movie[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [similarMovies, setSimilarMovies] = useState<TMDBMovie[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [trailerVideoId, setTrailerVideoId] = useState<string | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);
  const [backdropError, setBackdropError] = useState(false);
  const [posterError, setPosterError] = useState(false);
  
  // Tab state for Stream vs Download sources
  const [activeTab, setActiveTab] = useState<'stream' | 'download'>('stream');
  
  // Loading state for stream navigation
  const [isNavigatingToStream, setIsNavigatingToStream] = useState(false);
  
  // VidSrc availability check
  const [vidsrcAvailable, setVidsrcAvailable] = useState<boolean | null>(null);
  
  // YouTube API states
  const [youtubeApiLoaded, setYoutubeApiLoaded] = useState(false);
  const [isTrailerLoading, setIsTrailerLoading] = useState(false);
  const [trailerPlayError, setTrailerPlayError] = useState(false);
  const youtubePlayerRef = useRef<any>(null);
  const previousMovieIdRef = useRef<number | undefined>(undefined);
  
  const isTV = useIsTVDevice();
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const trailerButtonRef = useRef<HTMLDivElement>(null);
  const streamTabRef = useRef<HTMLButtonElement>(null);
  const downloadTabRef = useRef<HTMLButtonElement>(null);
  const streamSourceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const downloadSourceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const similarMoviesRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [focusedSimilarIndex, setFocusedSimilarIndex] = useState(-1);
  const [downloadSourcesReady, setDownloadSourcesReady] = useState(false);
  
  // Track current focus index
  const [currentFocusIndex, setCurrentFocusIndex] = useState(0);
  
  // Get stream sources count
  // For TV, always show vidsrc (even if not available) to maintain consistent focus indices
  const streamSources = useMemo(() => {
    if (isTV) {
      // Always show all sources for TV to maintain consistent focus navigation
      return getStreamSources();
    }
    // For non-TV, filter based on availability
    return getStreamSources().filter((source) => {
      if (source.id === 'vidsrc') {
        return vidsrcAvailable === true;
      }
      return true;
    });
  }, [vidsrcAvailable, isTV]);
  
  // Ref for Stream section header (non-focusable, but we track it)
  const streamHeaderRef = useRef<HTMLDivElement>(null);
  const downloadHeaderRef = useRef<HTMLDivElement>(null);
  
  // Build ordered array of focusable elements
  // For TV: Show both sections simultaneously, no tabs
  const focusableElements = useMemo(() => {
    const elements: HTMLElement[] = [];
    
    // Back button (always first)
    if (backButtonRef.current) {
      elements.push(backButtonRef.current);
    }
    
    // Trailer (conditional, second if exists)
    if (trailerVideoId && trailerButtonRef.current) {
      elements.push(trailerButtonRef.current);
    }
    
    // Stream sources (always include for TV)
    for (let i = 0; i < streamSources.length; i++) {
      const ref = streamSourceRefs.current[i];
      if (ref) {
        elements.push(ref);
      }
    }
    
    // Download sources (always include for TV, even if empty)
    for (let i = 0; i < botResults.length; i++) {
      const ref = downloadSourceRefs.current[i];
      if (ref) {
        elements.push(ref);
      }
    }
    
    debugLog('[TVNavigation] Focus array built:', {
      backButton: !!backButtonRef.current,
      trailer: trailerVideoId ? !!trailerButtonRef.current : false,
      streamSources: streamSources.length,
      downloadSources: botResults.length,
      totalElements: elements.length
    });
    
    return elements;
  }, [trailerVideoId, streamSources.length, botResults.length, downloadSourcesReady]);
  
  // Debounced scrollIntoView for better performance
  const scrollIntoViewRef = useRef<number | null>(null);
  const debouncedScrollIntoView = useCallback((element: HTMLElement) => {
    if (scrollIntoViewRef.current) {
      cancelAnimationFrame(scrollIntoViewRef.current);
    }
    
    scrollIntoViewRef.current = requestAnimationFrame(() => {
      element.scrollIntoView({ 
        behavior: isTV ? 'auto' : 'smooth', // Instant on TV
        block: 'center' 
      });
      scrollIntoViewRef.current = null;
    });
  }, [isTV]);
  
  // Initialize focus on back button when page loads
  useEffect(() => {
    if (isTV && backButtonRef.current && focusableElements.length > 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (backButtonRef.current) {
          backButtonRef.current.focus();
          setCurrentFocusIndex(0);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isTV, focusableElements.length]);
  
  // Handle keyboard navigation
  useEffect(() => {
    if (!isTV) return;
    
    // Allow navigation even if focus array is temporarily empty (during recalculation)
    // Navigation should work as long as we have at least the back button
    // The focus array will be rebuilt on next render
    
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      // Capture current focus index at the start of handler
      const currentIdx = currentFocusIndex;
      let newIndex = currentIdx;
      
      if (key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        // Simple sequential navigation for TV: just move to next element
        newIndex = Math.min(currentIdx + 1, focusableElements.length - 1);
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        // Simple sequential navigation for TV: just move to previous element
        newIndex = Math.max(currentIdx - 1, 0);
      } else if (key === 'ArrowRight') {
        // No special handling needed for TV - sequential navigation
        e.preventDefault();
        e.stopPropagation();
        newIndex = Math.min(currentIdx + 1, focusableElements.length - 1);
      } else if (key === 'ArrowLeft') {
        // No special handling needed for TV - sequential navigation
        e.preventDefault();
        e.stopPropagation();
        newIndex = Math.max(currentIdx - 1, 0);
      } else if (key === 'Escape' || key === 'Back' || key === 'XF86Back') {
        e.preventDefault();
        e.stopPropagation();
        onBack();
        return;
      } else {
        return; // Don't handle other keys
      }
      
      // Ensure we have a valid focus array before navigating
      if (focusableElements.length === 0) {
        // If focus array is empty, try to rebuild it or use fallback navigation
        if (backButtonRef.current && newIndex === 0) {
          backButtonRef.current.focus();
          setCurrentFocusIndex(0);
        }
        return;
      }
      
      if (newIndex !== currentIdx && newIndex >= 0 && newIndex < focusableElements.length && focusableElements[newIndex]) {
        // Use setTimeout to ensure state updates are processed
        setTimeout(() => {
          setCurrentFocusIndex(newIndex);
          const targetElement = focusableElements[newIndex];
          if (targetElement) {
            targetElement.focus();
            debouncedScrollIntoView(targetElement); // Use debounced version
            // Debug: Log the navigation
            debugLog('[TVNavigation] ArrowDown:', {
              from: currentIdx,
              to: newIndex,
              activeTab,
              focusArrayLength: focusableElements.length,
              baseSourceIndex: trailerVideoId ? 4 : 3,
              streamTabIndex: trailerVideoId ? 2 : 1
            });
          }
        }, 0);
      } else {
        // Debug: Log why navigation didn't happen
        debugLog('[TVNavigation] ArrowDown blocked:', {
          currentIdx,
          newIndex,
          focusArrayLength: focusableElements.length,
          hasTarget: newIndex >= 0 && newIndex < focusableElements.length ? !!focusableElements[newIndex] : false,
          activeTab,
          baseSourceIndex: trailerVideoId ? 4 : 3
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isTV, currentFocusIndex, focusableElements, trailerVideoId, activeTab, onBack, debouncedScrollIntoView]);
  
  // Update focus index when elements change (e.g., tab switch or sources load)
  useEffect(() => {
    if (focusableElements.length === 0) return;
    
    // If current index is out of bounds, adjust it
    if (currentFocusIndex >= focusableElements.length) {
      // If we're on a source card and tab changed, move to the active tab
      const streamTabIndex = trailerVideoId ? 2 : 1;
      const downloadTabIndex = trailerVideoId ? 3 : 2;
      
      // Determine which tab to focus based on activeTab
      const targetTabIndex = activeTab === 'stream' ? streamTabIndex : downloadTabIndex;
      
      debugLog('[TVNavigation] Adjusting out-of-bounds focus index:', {
        currentFocusIndex,
        focusArrayLength: focusableElements.length,
        targetTabIndex,
        activeTab
      });
      
      setCurrentFocusIndex(targetTabIndex);
      setTimeout(() => {
        if (focusableElements[targetTabIndex]) {
          focusableElements[targetTabIndex].focus();
        }
      }, 0);
    }
  }, [focusableElements, currentFocusIndex, activeTab, trailerVideoId]);
  
  // Force focus array recalculation when download sources load
  useEffect(() => {
    if (!isTV || activeTab !== 'download') return;
    
    // When download sources are loaded, ensure focus array is updated
    if (botResults.length > 0) {
      // Small delay to ensure refs are set
      const timer = setTimeout(() => {
        // Force a recalculation by checking if download sources are in focus array
        const downloadTabIndex = trailerVideoId ? 3 : 2;
        const baseSourceIndex = trailerVideoId ? 4 : 3;
        
        // Check if first download source is in focus array
        if (baseSourceIndex < focusableElements.length) {
          const firstDownloadSource = downloadSourceRefs.current[0];
          if (firstDownloadSource && focusableElements[baseSourceIndex] !== firstDownloadSource) {
            // Focus array needs to be updated - this will happen via useMemo dependencies
            // But we can trigger a focus update to ensure everything is synced
          }
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isTV, activeTab, botResults.length, focusableElements.length, trailerVideoId]);
  
  // Detect when download source refs are actually set in DOM
  useEffect(() => {
    if (!isTV || botResults.length === 0) {
      setDownloadSourcesReady(false);
      return;
    }
    
    // Check if refs are set after a short delay to allow DOM to update
    const timer = setTimeout(() => {
      let allRefsSet = true;
      for (let i = 0; i < botResults.length; i++) {
        if (!downloadSourceRefs.current[i]) {
          allRefsSet = false;
          break;
        }
      }
      
      if (allRefsSet) {
        setDownloadSourcesReady(true);
        debugLog('[TVNavigation] Download source refs are ready:', {
          count: botResults.length,
          allRefsSet: true
        });
      } else {
        // Retry after another short delay
        setTimeout(() => {
          let retryAllRefsSet = true;
          for (let i = 0; i < botResults.length; i++) {
            if (!downloadSourceRefs.current[i]) {
              retryAllRefsSet = false;
              break;
            }
          }
          if (retryAllRefsSet) {
            setDownloadSourcesReady(true);
            debugLog('[TVNavigation] Download source refs are ready (retry):', {
              count: botResults.length,
              allRefsSet: true
            });
          }
        }, 100);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isTV, botResults.length]);

  const movieId = useMemo(() => movie?.id, [movie?.id]);

  // Fetch trailer from TMDB
  const fetchTrailer = useCallback(async (tmdbId: number) => {
    try {
      const response = await fetch(getApiUrl('TMDB_VIDEOS', undefined, { movieId: tmdbId.toString() }));
      const data = await response.json();
      
      if (data.success && data.videoId) {
        setTrailerVideoId(data.videoId);
      } else {
        setTrailerVideoId(null);
      }
      } catch (error) {
        debugError('Error fetching trailer:', error);
        setTrailerVideoId(null);
      }
    }, []);

  // Fetch similar movies
  const fetchSimilarMovies = useCallback(async (tmdbId: number) => {
    setLoadingSimilar(true);
    try {
      const response = await fetch(getApiUrl('TMDB_SIMILAR', undefined, { movieId: tmdbId.toString() }));
      const data = await response.json();
      
      if (data.success && data.results) {
        // Show fewer similar movies on TV for better performance
        const count = isTV ? 6 : 12;
        setSimilarMovies(data.results.slice(0, count));
      }
    } catch (error) {
      debugError('Error fetching similar movies:', error);
    } finally {
      setLoadingSimilar(false);
    }
  }, [isTV]);

  // Search for movie versions on bot
  const handleSearchBot = useCallback(async () => {
    if (!movie) return;
    
    setIsSearching(true);
    setSearchError(null);
    
    try {
      const results = await onSearchBot(movie.title);
      
      setBotResults(results);
      setHasSearched(true);
      
      if (results.length === 0) {
        setSearchError('No versions found for this movie. Try a different search term in the header.');
      }
    } catch (error: any) {
      debugError('Search error:', error);
      setSearchError(error?.message || 'Failed to search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [movie, onSearchBot]);

  // Auto-trigger download search for TV when page loads
  useEffect(() => {
    if (isTV && movie && !hasSearched && botResults.length === 0) {
      // Auto-search for download sources on TV
      const timer = setTimeout(() => {
        handleSearchBot();
      }, 500); // Small delay to ensure page is ready
      return () => clearTimeout(timer);
    }
  }, [isTV, movie, hasSearched, botResults.length, handleSearchBot]);

  // Handle stream source navigation
  const handleStreamSourceClick = useCallback((sourceId: string) => {
    if (!movie) return;
    
    setIsNavigatingToStream(true);
    const params = new URLSearchParams({
      source: sourceId,
      title: movie.title,
      posterPath: movie.poster_path || '',
      backdropPath: movie.backdrop_path || '',
      overview: movie.overview || '',
      releaseDate: movie.release_date || '',
      voteAverage: movie.vote_average?.toString() || ''
    });
    window.location.href = `/watch/${movie.id}?${params.toString()}`;
  }, [movie]);

  // Load YouTube IFrame API script
  useEffect(() => {
    if (window.YT) {
      setYoutubeApiLoaded(true);
      return;
    }
    
    window.onYouTubeIframeAPIReady = () => {
      setYoutubeApiLoaded(true);
    };
    
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Helper function to create YouTube player
  const createYouTubePlayer = useCallback((elementId: string, videoId: string) => {
    if (youtubePlayerRef.current) {
      youtubePlayerRef.current.destroy();
      youtubePlayerRef.current = null;
    }
    
    const player = new window.YT.Player(elementId, {
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        mute: 0,
        controls: 1,
        start: 0,
        loop: 1,
        playlist: videoId,
        showinfo: 0,
        rel: 0,
        modestbranding: 1
      },
      events: {
        onReady: (event: any) => {
          youtubePlayerRef.current = event.target;
          setIsTrailerLoading(false);
          setTrailerPlayError(false);
        },
        onStateChange: (event: any) => {
          if (event.data === window.YT.PlayerState.ENDED) {
            if (youtubePlayerRef.current) {
              youtubePlayerRef.current.seekTo(0);
              youtubePlayerRef.current.playVideo();
            }
          }
        },
        onError: () => {
          setTrailerPlayError(true);
          setIsTrailerLoading(false);
          setShowTrailer(false);
        }
      }
    });
  }, []);

  // Check VidSrc availability when movie changes
  useEffect(() => {
    if (movieId) {
      const checkVidsrcAvailability = async () => {
        setVidsrcAvailable(null);
        try {
          const response = await fetch(getApiUrl('VIDSRC_CHECK', movieId.toString()));
          const data = await response.json();
          setVidsrcAvailable(data.success ? data.available : false);
        } catch (error) {
          setVidsrcAvailable(false);
        }
      };
      checkVidsrcAvailability();
    }
  }, [movieId]);

  // Initialize searches when movie changes - only trigger when movieId actually changes
  useEffect(() => {
    if (movie && movieId && movieId !== previousMovieIdRef.current) {
      previousMovieIdRef.current = movieId;
      
      // Reset all state
      setBotResults([]);
      setHasSearched(false);
      setSearchError(null);
      setSimilarMovies([]);
      setTrailerVideoId(null);
      setShowTrailer(false);
      setIsTrailerLoading(false);
      setBackdropError(false);
      setPosterError(false);
      setActiveTab('stream');
      
      handleSearchBot();
      fetchTrailer(movieId);
      
      // Add small delay only for similar movies to ensure backend is ready
      const timer = setTimeout(() => {
        fetchSimilarMovies(movieId);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [movie, movieId, handleSearchBot, fetchSimilarMovies, fetchTrailer]);

  // Initialize YouTube player when trailer video ID changes
  useEffect(() => {
    if (!trailerVideoId || !youtubeApiLoaded) return;
    
    setIsTrailerLoading(true);
    setTrailerPlayError(false);
    const elementId = `youtube-player-tv-${trailerVideoId}`;
    
    const timer = setTimeout(() => {
      setShowTrailer(true);
      // Poll for element with exponential backoff
      let attempts = 0;
      const maxAttempts = 20;
      const pollForElement = () => {
        const element = document.getElementById(elementId);
        if (element) {
          createYouTubePlayer(elementId, trailerVideoId);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(pollForElement, Math.min(100 * attempts, 500));
        } else {
          setTrailerPlayError(true);
        }
      };
      setTimeout(pollForElement, 50);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [trailerVideoId, youtubeApiLoaded, createYouTubePlayer]);

  // Cleanup YouTube player when movie changes
  useEffect(() => {
    if (!movie && youtubePlayerRef.current) {
      youtubePlayerRef.current.destroy();
      youtubePlayerRef.current = null;
      setShowTrailer(false);
      setIsTrailerLoading(false);
    }
  }, [movie]);

  if (!movie) return null;

  // Optimize image sizes for TV - use smaller sizes for better performance
  const backdropUrl = useMemo(() => {
    if (!movie?.backdrop_path) return '';
    const size = isTV ? IMAGE_SIZES.backdrop.medium : IMAGE_SIZES.backdrop.large;
    return getImageUrl(movie.backdrop_path, size);
  }, [movie?.backdrop_path, isTV]);

  const posterUrl = useMemo(() => {
    if (!movie?.poster_path) return '';
    const size = isTV ? IMAGE_SIZES.poster.medium : IMAGE_SIZES.poster.large;
    return getImageUrl(movie.poster_path, size);
  }, [movie?.poster_path, isTV]);

  const year = movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A';
  const rating = movie.vote_average ? movie.vote_average.toFixed(1) : 'N/A';
  const runtime = (movie as any).runtime ? `${(movie as any).runtime} min` : 'N/A';

  return (
    <div className="min-h-screen bg-[#141414] text-white">
      {/* Background with gradient */}
      <div className="fixed inset-0">
        <img
          src={backdropError ? '/placeholder-movie.png' : backdropUrl}
          alt={movie.title}
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
          onError={() => {
            if (!backdropError) {
              setBackdropError(true);
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141414] via-[#141414]/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen pt-20 px-8 lg:px-16 pb-12">
        {/* Navigation Loading Overlay */}
        {isNavigatingToStream && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <div className="text-center">
              <div className="loading-spinner mb-4"></div>
              <p className="text-white text-lg font-medium">Opening stream...</p>
              <p className="text-gray-400 text-sm mt-2">Please wait while we prepare your video</p>
            </div>
          </motion.div>
        )}

        {/* Back Button */}
        <motion.button
          ref={backButtonRef}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-white hover:text-red-600 transition-colors focus:ring-4 focus:ring-red-600/50 rounded-lg p-2"
          tabIndex={isTV ? 0 : undefined}
          onFocus={() => setCurrentFocusIndex(0)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onBack();
            }
          }}
        >
          <ArrowLeft className="w-6 h-6" />
          <span className="text-lg font-medium">Back to Browse</span>
        </motion.button>

        {/* Hero Section: Title, Poster, and Metadata */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
          {/* Poster */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3 flex justify-center lg:justify-start"
          >
            <img
              src={posterError ? '/placeholder-movie.png' : posterUrl}
              alt={movie.title}
              className="w-full max-w-xs rounded-xl shadow-2xl ring-2 ring-white/10"
              loading="eager"
              decoding="async"
              onError={() => {
                if (!posterError) {
                  setPosterError(true);
                }
              }}
            />
          </motion.div>

          {/* Title and Metadata */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-9 space-y-4"
          >
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">{movie.title}</h1>
            
            <div className="flex flex-wrap items-center gap-4 text-base lg:text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{year}</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span>{rating}</span>
              </div>
              {runtime !== 'N/A' && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{runtime}</span>
                </div>
              )}
              <Badge className="bg-white/20 hover:bg-white/30 text-base px-3 py-1">HD</Badge>
            </div>

            {/* Overview - Compact */}
            <div className="pt-2">
              <p className="text-gray-300 leading-relaxed text-base lg:text-lg max-w-3xl line-clamp-3">
                {movie.overview || 'No overview available.'}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Trailer Section - Large and Immersive (Netflix-style) */}
        {trailerVideoId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 -mx-8 lg:-mx-16"
          >
            <div 
              ref={trailerButtonRef}
              className="aspect-video w-full rounded-lg overflow-hidden ring-1 ring-white/20 relative shadow-2xl focus:ring-4 focus:ring-red-600/50 focus:outline-none"
              tabIndex={isTV ? 0 : undefined}
              role="button"
              aria-label="Trailer video"
              onFocus={() => {
                // Trailer is at index 1 (after back button at 0) if it exists
                if (trailerVideoId) {
                  setCurrentFocusIndex(1);
                }
              }}
            >
              {showTrailer && !trailerPlayError && (
                <div
                  id={`youtube-player-tv-${trailerVideoId}`}
                  className="w-full h-full"
                />
              )}
              {isTrailerLoading && (
                <div className="absolute inset-0 bg-black/50">
                  <div className="w-full h-full rounded-lg bg-gray-700/50 animate-pulse-slow" />
                </div>
              )}
              {trailerPlayError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <button
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${trailerVideoId}`, '_blank')}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors focus:ring-4 focus:ring-red-600/50"
                    tabIndex={-1}
                  >
                    Play Trailer on YouTube
                  </button>
                </div>
              )}
              {!showTrailer && !trailerPlayError && (
                <img
                  src={backdropError ? '/placeholder-movie.png' : backdropUrl}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Main Content */}
          <div className="lg:col-span-8 space-y-6">

            {/* Available Versions Section - TV: Show both sections simultaneously */}
            {isTV ? (
              /* TV Layout: Both sections visible */
              <div className="space-y-8">
                {/* Stream Sources Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-4"
                >
                  <div ref={streamHeaderRef} className="flex items-center gap-3">
                    <Monitor className="w-6 h-6 text-red-600" />
                    <h3 className="text-2xl lg:text-3xl font-bold">Stream Sources</h3>
                  </div>
                  
                  <div className="space-y-3">
                  {streamSources.map((source, index) => {
                    // For TV, show vidsrc even if not available (but visually indicate it's unavailable)
                    const isVidsrcUnavailable = source.id === 'vidsrc' && isTV && vidsrcAvailable === false;
                    
                    return (
                      <motion.div
                        key={source.id}
                        ref={(el) => {
                          streamSourceRefs.current[index] = el;
                        }}
                        initial={{ opacity: isTV ? 1 : 0, x: isTV ? 0 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: isTV ? 0.1 : 0.3, delay: isTV ? 0 : index * 0.05 }}
                        className={`glass-card p-4 rounded-lg cursor-pointer transition-all border border-gray-700/30 hover:border-gray-600/50 focus:border-red-600 focus:ring-4 focus:ring-red-600/50 tv-focusable-item ${
                          isVidsrcUnavailable ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={() => {
                          if (!isVidsrcUnavailable) {
                            handleStreamSourceClick(source.id);
                          }
                        }}
                        role="button"
                        tabIndex={isTV ? 0 : undefined}
                        aria-label={`Stream via ${source.name}${isVidsrcUnavailable ? ' (unavailable)' : ''}`}
                        onFocus={() => {
                          // Find the actual position of this element in focusableElements array
                          // This ensures correct navigation even if some refs are null
                          const currentRef = streamSourceRefs.current[index];
                          if (!currentRef) return;
                          
                          const actualIndex = focusableElements.findIndex(el => el === currentRef);
                          if (actualIndex !== -1) {
                            setCurrentFocusIndex(actualIndex);
                          } else {
                            // Fallback: count how many valid stream source refs come before this one
                            let validStreamIndex = 0;
                            for (let i = 0; i < index; i++) {
                              if (streamSourceRefs.current[i]) {
                                validStreamIndex++;
                              }
                            }
                            // Calculate base: back(0) + trailer(1 if exists) + valid stream sources before this
                            const baseIndex = trailerVideoId ? 2 : 1;
                            setCurrentFocusIndex(baseIndex + validStreamIndex);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isVidsrcUnavailable) {
                              handleStreamSourceClick(source.id);
                            }
                          }
                        }}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-xl">{source.icon}</span>
                              <div>
                                <h4 className="font-semibold text-white text-base">{source.name}</h4>
                                <p className="text-xs text-gray-400">{source.description}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge className="bg-green-600 text-white border-green-500 text-xs">
                                ⚡ Instant Play
                              </Badge>
                              {source.badge && (
                                <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30 text-xs">
                                  {source.badge}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 flex-shrink-0 shadow-lg"
                            aria-label={`Stream via ${source.name}`}
                          >
                            <Play className="w-4 h-4 fill-current" />
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                  </div>
                </motion.div>

                {/* Download Sources Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  <div ref={downloadHeaderRef} className="flex items-center gap-3">
                    <Download className="w-6 h-6 text-red-600" />
                    <h3 className="text-2xl lg:text-3xl font-bold">Download Sources</h3>
                  </div>
                  
                  {isSearching ? (
                    <div className="space-y-3">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="glass-card p-4 rounded-lg">
                          <Skeleton className="h-4 w-3/4 mb-2 bg-gray-700/50 animate-pulse-slow" />
                          <div className="flex gap-2">
                            <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                            <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                            <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                            <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : searchError ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="glass-card p-8 rounded-lg text-center border border-red-600/30"
                    >
                      <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <p className="text-gray-300 mb-2 font-medium">No Versions Available</p>
                      <p className="text-sm text-gray-400">{searchError}</p>
                      <Button
                        onClick={handleSearchBot}
                        variant="outline"
                        className="mt-6 border-gray-600 hover:bg-white/5"
                      >
                        Try Again
                      </Button>
                    </motion.div>
                  ) : botResults.length > 0 ? (
                    <div className="space-y-3">
                      {botResults.map((result, index) => (
                        <motion.div
                          key={result.id}
                          ref={(el) => {
                            downloadSourceRefs.current[index] = el;
                            // If this is the last item and all refs are now set, trigger update
                            if (el && index === botResults.length - 1) {
                              // Small delay to ensure all refs in the batch are set
                              setTimeout(() => {
                                let allSet = true;
                                for (let i = 0; i < botResults.length; i++) {
                                  if (!downloadSourceRefs.current[i]) {
                                    allSet = false;
                                    break;
                                  }
                                }
                                if (allSet) {
                                  setDownloadSourcesReady(true);
                                }
                              }, 50);
                            }
                          }}
                          initial={{ opacity: isTV ? 1 : 0, x: isTV ? 0 : -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: isTV ? 0.1 : 0.3, delay: isTV ? 0 : index * 0.05 }}
                          className="glass-card p-4 rounded-lg cursor-pointer transition-all border border-gray-700/30 hover:border-gray-600/50 focus:border-red-600 focus:ring-4 focus:ring-red-600/50 tv-focusable-item"
                          onClick={() => onSelectVersion(result)}
                          role="button"
                          tabIndex={isTV ? 0 : undefined}
                          aria-label={`Play ${result.title}`}
                        onFocus={() => {
                          // Find the actual position of this element in focusableElements array
                          const currentRef = downloadSourceRefs.current[index];
                          if (!currentRef) return;
                          
                          const actualIndex = focusableElements.findIndex(el => el === currentRef);
                          if (actualIndex !== -1) {
                            setCurrentFocusIndex(actualIndex);
                          } else {
                            // Fallback: count valid download source refs before this one
                            let validDownloadIndex = 0;
                            for (let i = 0; i < index; i++) {
                              if (downloadSourceRefs.current[i]) {
                                validDownloadIndex++;
                              }
                            }
                            // Calculate base: back(0) + trailer(1 if exists) + all stream sources + valid download sources before this
                            const baseIndex = (trailerVideoId ? 2 : 1) + streamSources.length;
                            setCurrentFocusIndex(baseIndex + validDownloadIndex);
                          }
                        }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onSelectVersion(result);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold mb-2 text-white truncate text-base">{result.title}</h4>
                              <div className="flex flex-wrap gap-2">
                                {result.cached && (
                                  <Badge className="bg-green-600 text-white border-green-500 text-xs">
                                    ⚡ CACHED
                                  </Badge>
                                )}
                                <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30 text-xs">
                                  {result.quality}
                                </Badge>
                                <Badge variant="secondary" className="bg-blue-600/20 text-blue-400 border-blue-600/30 text-xs">
                                  {result.size}
                                </Badge>
                                <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30 text-xs">
                                  {result.format}
                                </Badge>
                                <Badge variant="secondary" className="bg-purple-600/20 text-purple-400 border-purple-600/30 text-xs">
                                  {result.year}
                                </Badge>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 flex-shrink-0 shadow-lg"
                              aria-label="Play this version"
                            >
                              <Play className="w-4 h-4 fill-current" />
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : hasSearched ? (
                    <p className="text-gray-400 py-8 text-base">No versions available.</p>
                  ) : !hasSearched && movie ? (
                    <div className="glass-card p-6 rounded-lg text-center border border-gray-700/30">
                      <Download className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">Searching for downloadable sources...</p>
                    </div>
                  ) : null}
                </motion.div>
              </div>
            ) : (
              /* Non-TV Layout: Tab-based (original design) */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl lg:text-3xl font-bold">Available Versions</h3>
                  {isSearching && activeTab === 'download' && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <div className="loading-spinner w-4 h-4 border-2" />
                      <span>Searching...</span>
                    </div>
                  )}
                </div>
                
                {/* Tab Navigation */}
                <div className="flex gap-1 bg-gray-800/50 p-1 rounded-lg max-w-md">
                  <button
                    ref={streamTabRef}
                    onClick={() => setActiveTab('stream')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-all text-sm ${
                      activeTab === 'stream'
                        ? 'bg-red-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Monitor className="w-4 h-4" />
                    <span className="font-medium">Stream</span>
                  </button>
                  <button
                    ref={downloadTabRef}
                    onClick={() => {
                      setActiveTab('download');
                      if (!hasSearched && movie) {
                        handleSearchBot();
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-all text-sm ${
                      activeTab === 'download'
                        ? 'bg-red-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    <span className="font-medium">Download</span>
                  </button>
                </div>
                
                {/* Tab Content */}
                {activeTab === 'stream' ? (
                  <div className="space-y-3">
                    {streamSources.map((source, index) => {
                      const isVidsrcUnavailable = source.id === 'vidsrc' && vidsrcAvailable === false;
                      
                      return (
                        <motion.div
                          key={source.id}
                          ref={(el) => {
                            streamSourceRefs.current[index] = el;
                          }}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`glass-card p-4 rounded-lg cursor-pointer transition-all border border-gray-700/30 hover:border-gray-600/50 ${
                            isVidsrcUnavailable ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => {
                            if (!isVidsrcUnavailable) {
                              handleStreamSourceClick(source.id);
                            }
                          }}
                          role="button"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-xl">{source.icon}</span>
                                <div>
                                  <h4 className="font-semibold text-white text-base">{source.name}</h4>
                                  <p className="text-xs text-gray-400">{source.description}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Badge className="bg-green-600 text-white border-green-500 text-xs">
                                  ⚡ Instant Play
                                </Badge>
                                {source.badge && (
                                  <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30 text-xs">
                                    {source.badge}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 flex-shrink-0 shadow-lg"
                              aria-label={`Stream via ${source.name}`}
                            >
                              <Play className="w-4 h-4 fill-current" />
                            </Button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    {isSearching ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="glass-card p-4 rounded-lg">
                            <Skeleton className="h-4 w-3/4 mb-2 bg-gray-700/50 animate-pulse-slow" />
                            <div className="flex gap-2">
                              <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                              <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                              <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                              <Skeleton className="h-5 w-16 bg-gray-700/50 animate-pulse-slow" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : searchError ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass-card p-8 rounded-lg text-center border border-red-600/30"
                      >
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <p className="text-gray-300 mb-2 font-medium">No Versions Available</p>
                        <p className="text-sm text-gray-400">{searchError}</p>
                        <Button
                          onClick={handleSearchBot}
                          variant="outline"
                          className="mt-6 border-gray-600 hover:bg-white/5"
                        >
                          Try Again
                        </Button>
                      </motion.div>
                    ) : botResults.length > 0 ? (
                      <div className="space-y-3">
                        {botResults.map((result, index) => (
                          <motion.div
                            key={result.id}
                            ref={(el) => {
                              downloadSourceRefs.current[index] = el;
                            }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="glass-card p-4 rounded-lg cursor-pointer transition-all border border-gray-700/30 hover:border-gray-600/50"
                            onClick={() => onSelectVersion(result)}
                            role="button"
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold mb-2 text-white truncate text-base">{result.title}</h4>
                                <div className="flex flex-wrap gap-2">
                                  {result.cached && (
                                    <Badge className="bg-green-600 text-white border-green-500 text-xs">
                                      ⚡ CACHED
                                    </Badge>
                                  )}
                                  <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-600/30 text-xs">
                                    {result.quality}
                                  </Badge>
                                  <Badge variant="secondary" className="bg-blue-600/20 text-blue-400 border-blue-600/30 text-xs">
                                    {result.size}
                                  </Badge>
                                  <Badge variant="secondary" className="bg-green-600/20 text-green-400 border-green-600/30 text-xs">
                                    {result.format}
                                  </Badge>
                                  <Badge variant="secondary" className="bg-purple-600/20 text-purple-400 border-purple-600/30 text-xs">
                                    {result.year}
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 flex-shrink-0 shadow-lg"
                                aria-label="Play this version"
                              >
                                <Play className="w-4 h-4 fill-current" />
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : hasSearched ? (
                      <p className="text-gray-400 py-8 text-base">No versions available.</p>
                    ) : null}
                  </>
                )}
              </motion.div>
            )}

            {/* Similar Movies */}
            {(similarMovies.length > 0 || loadingSimilar) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-4"
              >
                <h3 className="text-2xl lg:text-3xl font-bold">More Like This</h3>
                
                {loadingSimilar ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="aspect-[2/3] w-full rounded-lg bg-gray-700/50 animate-pulse-slow" />
                        <Skeleton className="h-4 w-full bg-gray-700/50 animate-pulse-slow" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4" data-similar-movies>
                    {similarMovies.map((similarMovie, index) => (
                      <motion.button
                        key={similarMovie.id}
                        ref={(el) => {
                          similarMoviesRefs.current[index] = el;
                        }}
                        initial={{ opacity: isTV ? 1 : 0, scale: isTV ? 1 : 0.9 }}
                        animate={{ opacity: 1, scale: isTV && focusedSimilarIndex === index ? 1.05 : 1 }}
                        transition={{ duration: isTV ? 0.1 : 0.3, delay: isTV ? 0 : index * 0.05 }}
                        onClick={() => onMovieClick?.(similarMovie)}
                        className={`group relative aspect-[2/3] rounded-lg overflow-hidden ring-2 ring-white/10 hover:ring-red-600/50 transition-all focus:ring-4 focus:ring-red-600/50 ${isTV && focusedSimilarIndex === index ? 'tv-focused' : ''}`}
                        tabIndex={isTV ? 0 : undefined}
                        onFocus={() => setFocusedSimilarIndex(index)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onMovieClick?.(similarMovie);
                          }
                        }}
                      >
                        <img
                          src={getImageUrl(similarMovie.poster_path, IMAGE_SIZES.poster.medium)}
                          alt={similarMovie.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          onError={(e) => {
                            if (!e.currentTarget.src.includes('placeholder-movie.png')) {
                              e.currentTarget.src = '/placeholder-movie.png';
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                          <Play className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 fill-current" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black to-transparent">
                          <h4 className="text-sm font-medium text-white line-clamp-2">{similarMovie.title}</h4>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Right: Movie Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="lg:col-span-4 space-y-6"
          >
            <div className="space-y-4 text-sm bg-gray-900/50 rounded-xl p-5 border border-gray-800/50">
              <h4 className="font-bold text-lg text-white mb-4">Movie Details</h4>
              <div className="space-y-3">
                <div>
                  <span className="text-gray-400 block mb-1 text-xs">Original Title</span>
                  <p className="font-medium text-white text-base">{movie.original_title}</p>
                </div>
                <div className="border-t border-gray-800/50 pt-3">
                  <span className="text-gray-400 block mb-1 text-xs">Release Date</span>
                  <p className="font-medium text-white text-base">{movie.release_date || 'N/A'}</p>
                </div>
                <div className="border-t border-gray-800/50 pt-3">
                  <span className="text-gray-400 block mb-1 text-xs">Rating</span>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <p className="font-medium text-white text-base">{rating} / 10</p>
                  </div>
                </div>
                <div className="border-t border-gray-800/50 pt-3">
                  <span className="text-gray-400 block mb-1 text-xs">Popularity</span>
                  <p className="font-medium text-white text-base">{movie.popularity?.toFixed(0) || 'N/A'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

